import { useCallback, useMemo } from 'react';
import { useDynamicConfig } from '@statsig/react-bindings';

import { DynamicConfigs } from 'modules/common/constants/featuregates';
import { calculateRewardBalance as calcReward, DEFAULT_REFERRAL_REWARD_TIERS } from 'modules/common/constants/referral';

const DEFAULTS = {
    enabled: true,
    end_date: '2026-04-30T23:59:59Z',
    deadline_display: 'April 30',
    discount_label: '1 month free',
    discount_value: '$349',
    scorp_reward: 600,
    llc_reward: 300,
    base_scorp_reward: 600,
};

function parseTiers(raw: unknown): Record<number, number> {
    if (typeof raw !== 'object' || raw === null) {
        return DEFAULT_REFERRAL_REWARD_TIERS;
    }
    const result = Object.entries(raw as Record<string, unknown>).reduce<Record<number, number>>((acc, [k, v]) => {
        const num = Number(k);
        if (!Number.isNaN(num) && typeof v === 'number') {
            acc[num] = v;
        }
        return acc;
    }, {});
    return Object.keys(result).length > 0 ? result : DEFAULT_REFERRAL_REWARD_TIERS;
}

export default function useReferralPromoConfig() {
    const config = useDynamicConfig(DynamicConfigs.ReferralPromoConfig);

    const enabled = config.get('enabled', DEFAULTS.enabled) as boolean;
    const startDate = String(config.get('start_date', '')).trim();
    const endDate = String(config.get('end_date', DEFAULTS.end_date)).trim();
    const deadlineDisplay = String(config.get('deadline_display', DEFAULTS.deadline_display)).trim();
    const discountLabel = String(config.get('discount_label', DEFAULTS.discount_label)).trim();
    const discountValue = String(config.get('discount_value', DEFAULTS.discount_value)).trim();
    const numberMonthsOff = Number(config.get('number_months_off', 2)) || 2;
    const rawScorpReward = Number(config.get('scorp_reward', DEFAULTS.scorp_reward));
    const scorpReward = Number.isFinite(rawScorpReward) ? rawScorpReward : DEFAULTS.scorp_reward;
    const rawLlcReward = Number(config.get('llc_reward', DEFAULTS.llc_reward));
    const llcReward = Number.isFinite(rawLlcReward) ? rawLlcReward : DEFAULTS.llc_reward;
    const rawBaseScorpReward = Number(config.get('base_scorp_reward', DEFAULTS.base_scorp_reward));
    const baseScorpReward = Number.isFinite(rawBaseScorpReward) ? rawBaseScorpReward : DEFAULTS.base_scorp_reward;

    const rewardTiers = useMemo(
        () => parseTiers(config.get('referral_reward_tiers', DEFAULT_REFERRAL_REWARD_TIERS)),
        [config]
    );

    const calculateRewardBalance = useCallback(
        (numberOfFriends: number) => calcReward(numberOfFriends, rewardTiers),
        [rewardTiers]
    );

    const isActive = (() => {
        if (!enabled) {
            return false;
        }
        const now = new Date();
        if (startDate && !Number.isNaN(new Date(startDate).getTime()) && new Date(startDate) > now) {
            return false;
        }
        if (endDate && !Number.isNaN(new Date(endDate).getTime()) && new Date(endDate) < now) {
            return false;
        }
        return true;
    })();

    return {
        isActive,
        deadlineDisplay,
        discountLabel,
        discountValue,
        numberMonthsOff,
        scorpReward,
        llcReward,
        baseScorpReward,
        endDate,
        rewardTiers,
        calculateRewardBalance,
    };
}
